import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Toast from '../support/Toast';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { isOwner } from '../../utils/RoleManager';
import useCompanyClosureRevert from './useCompanyClosureRevert';
import { formatClosureShortDate } from './companyClosure';
import styles from './CompanyClosureBanner.module.css';

const PAYWALL_PATH = '/subscription/plans';

/**
 * Banner do encerramento da conta (LGPD, checkpoint 4), montado no Header como
 * o PastDueBanner. Aparece em todas as telas só enquanto o ciclo está em
 * carência (grace) E a conta ainda tem acesso; bloqueada, quem avisa é o
 * bloco do paywall (CompanyClosurePaywallBlock). Não pode ser fechado.
 * Dono: "Exportar dados" e, quando cabe, "Manter conta". Outros papéis: só o texto.
 */
const CompanyClosureBanner = () => {
    // isOwner() lê o DataProvider (hook): só no corpo do componente
    const owner = isOwner();
    const navigate = useNavigate();
    const location = useLocation();
    const { closure, hasActiveSubscription } = useSubscription();
    const { reverting, revert } = useCompanyClosureRevert();
    const [toast, setToast] = useState(null);

    const visible = closure?.state === 'grace'
        && hasActiveSubscription()
        && location.pathname !== PAYWALL_PATH;

    const handleRevert = async () => {
        if (reverting) return;
        const result = await revert();
        setToast(result.success
            ? { severity: 'success', message: 'Encerramento cancelado. A conta continua ativa.' }
            : { severity: 'error', message: result.error });
    };

    const toastElement = (
        <Toast
            open={!!toast}
            message={toast?.message || ''}
            severity={toast?.severity || 'success'}
            onClose={() => setToast(null)}
        />
    );

    if (!visible) return toastElement;

    const requested = formatClosureShortDate(closure.requested_at);
    const scheduled = formatClosureShortDate(closure.scheduled_at);
    const start = closure.origin === 'owner_request'
        ? `Encerramento solicitado${requested ? ` em ${requested}` : ''}.`
        : `Conta em encerramento${requested ? ` desde ${requested}` : ''}.`;
    const end = scheduled
        ? ` Todos os dados serão eliminados em ${scheduled}.`
        : ' Ao fim da carência, todos os dados serão eliminados.';

    return (
        <>
            <div className={styles.banner}>
                <span className={styles.message}>
                    {start}{end}{!owner && ' Fale com o proprietário da conta.'}
                </span>
                {owner && (
                    <div className={styles.actions}>
                        <button type="button" className={styles.secondaryButton} onClick={() => navigate('/system/backup')}>
                            Exportar dados
                        </button>
                        {closure.can_revert === true && (
                            <button type="button" className={styles.ctaButton} onClick={handleRevert} disabled={reverting}>
                                {reverting ? 'Aguarde...' : 'Manter conta'}
                            </button>
                        )}
                    </div>
                )}
            </div>
            {toastElement}
        </>
    );
};

export default CompanyClosureBanner;
